/**
 * Field-level validation errors — the `fieldErrors` half of the error shape
 * docs/08 has specified since v1.0 and nothing implemented.
 *
 * Matt, 2026-08-26, on the QA sheet: *"Creating a new vendor and clicking
 * 'create vendor' I get invalid payload error"*. He was right, and the reason he
 * could not get further is that the API threw the answer away: every route
 * answered `sendError(reply, 400, 'VALIDATION', 'Invalid vendor payload')` while
 * zod knew exactly which field had failed and why. Reproduced against the
 * compiled schema — `website: 'carolinawaste.com'` fails `z.url()`, because a
 * URL without a scheme is not a URL — so the person who typed a perfectly
 * ordinary domain name is told the payload is invalid and given no field to fix.
 *
 * Two rules this module exists to keep:
 *
 * 1. **The server names the field.** docs/03 §2: errors are "specific and
 *    actionable, field-anchored where possible". A path is the only thing that
 *    lets a form anchor one.
 * 2. **The message says what to do.** zod's defaults say what happened
 *    ("Invalid URL"); a data-entry app has to say what to type instead. The
 *    mapping is here rather than in each screen so one wrong value cannot be
 *    explained two ways in two places.
 */

import type { z } from 'zod';

import { fieldLabel } from '../field-labels.ts';

export interface FieldError {
  /**
   * The dotted contract path — `website`, `changes.coiExpiration`,
   * `propertyIds.2`. EMPTY for an error about the payload as a whole, which a
   * form shows in its banner rather than against a control.
   */
  readonly path: string;
  readonly message: string;
}

/**
 * zod's message, replaced where a data-entry app can do better.
 *
 * Deliberately narrow: only the codes that a person can actually act on get a
 * rewrite. Everything else keeps zod's own wording, because inventing prose for
 * an issue nobody has seen is how error copy drifts from what the schema means.
 */
export function friendlyIssueMessage(issue: z.core.$ZodIssue): string {
  if (issue.code === 'invalid_format') {
    const format = (issue as { format?: string }).format;
    if (format === 'url') return 'Enter a full web address, including https://';
    if (format === 'email') return 'Enter an email address, like name@example.com';
    if (format === 'date') return 'Enter a date as YYYY-MM-DD';
    if (format === 'datetime') return 'Enter a date and time';
  }
  if (issue.code === 'too_big') {
    const max = (issue as { maximum?: number | bigint }).maximum;
    if ((issue as { origin?: string }).origin === 'string' && typeof max === 'number') {
      return `Keep this to ${String(max)} characters or fewer`;
    }
    if (typeof max === 'number') return `Enter ${String(max)} or less`;
  }
  if (issue.code === 'too_small') {
    const min = (issue as { minimum?: number | bigint }).minimum;
    if ((issue as { origin?: string }).origin === 'string') {
      return min === 1 ? 'This is required' : `Use at least ${String(min)} characters`;
    }
    if (typeof min === 'number') return `Enter ${String(min)} or more`;
  }
  if (issue.code === 'invalid_type') {
    const received = (issue as { input?: unknown }).input;
    if (received === undefined || received === null) return 'This is required';
  }
  if (issue.code === 'invalid_value' || issue.code === 'invalid_union') {
    return 'Choose one of the offered options';
  }
  /**
   * `.strict()` refusing a key is NOT the user's mistake — the client sent a
   * field the contract does not carry, which is a build that has drifted from
   * its server. Saying "reload" is the only actionable thing, and blaming the
   * typing would send somebody hunting through a form for a fault that is not
   * in it.
   */
  if (issue.code === 'unrecognized_keys') {
    return 'This form is out of date — reload the page and try again';
  }
  return issue.message;
}

/** Every issue in a failed parse, as paths a form can anchor on. */
export function toFieldErrors(error: z.ZodError): readonly FieldError[] {
  return error.issues.map((issue) => ({
    path: issue.path.map((segment) => String(segment)).join('.'),
    message: friendlyIssueMessage(issue),
  }));
}

/**
 * The path a FORM knows the field by.
 *
 * An update contract wraps its fields in `changes` (docs/03 §2b's PATCH rule),
 * so the server's path is `changes.coiExpiration` while the form's control is
 * `coiExpiration`. The envelope is plumbing; stripping it here means no screen
 * has to know it exists.
 */
export function formFieldPath(path: string): string {
  return path.startsWith('changes.') ? path.slice('changes.'.length) : path;
}

/**
 * A path as a person reads it. `propertyIds.2` is the third item of a list, and
 * a bare "2" beside a label reads as a value rather than as a position.
 */
export function fieldErrorLabel(path: string): string {
  const segments = formFieldPath(path).split('.');
  const last = segments[segments.length - 1] ?? '';
  if (/^\d+$/.test(last) && segments.length > 1) {
    const parent = segments[segments.length - 2] ?? '';
    return `${fieldLabel(parent)} (item ${String(Number(last) + 1)})`;
  }
  return fieldLabel(last);
}

/**
 * The first message per path, for handing straight to a field's `error` prop.
 *
 * FIRST rather than joined: two messages under one input is a stack of red text
 * pushing the next field down the page, and the second is almost always a
 * consequence of the first.
 */
export function fieldErrorMap(errors: readonly FieldError[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const error of errors) {
    if (error.path === '') continue;
    map[formFieldPath(error.path)] ??= error.message;
  }
  return map;
}

/**
 * The form-level sentence: what to say in the banner above the form.
 *
 * A form that anchors its errors still needs this, because the failing field
 * may be on a tab the reader cannot see (docs/03 §5's tabbed-form rule), and
 * "Website — Enter a full web address, including https://" read at the top is
 * what tells them where to go.
 */
export function fieldErrorSummary(errors: readonly FieldError[]): string | undefined {
  if (errors.length === 0) return undefined;
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const error of errors) {
    const key = `${error.path}|${error.message}`;
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(
      error.path === '' ? error.message : `${fieldErrorLabel(error.path)} — ${error.message}`,
    );
  }
  return parts.join('. ');
}
